import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Durées configurables (en millisecondes)
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes d'inactivité avant déconnexion
const WARNING_BEFORE_LOGOUT = 20 * 1000; // Afficher l'avertissement 20 secondes avant
const TOKEN_REFRESH_INTERVAL = 12 * 60 * 1000; // Rafraîchir le token toutes les 12 minutes si actif

// Événements considérés comme activité utilisateur
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel',
] as const;

interface UseInactivityTimeoutOptions {
  enabled?: boolean;
  onLogout?: () => void;
}

interface UseInactivityTimeoutReturn {
  showWarning: boolean;
  secondsRemaining: number;
  extendSession: () => void;
  logout: () => void;
}

export function useInactivityTimeout({
  enabled = true,
  onLogout,
}: UseInactivityTimeoutOptions = {}): UseInactivityTimeoutReturn {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(20);

  const { refreshToken, setTokens, logout: storeLogout, isAuthenticated } = useAuthStore();

  // Refs pour les timers
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const lastTokenRefreshRef = useRef<number>(Date.now());
  const isRefreshingRef = useRef(false);

  // Fonction pour rafraîchir le token de manière proactive
  const refreshTokenProactively = useCallback(async () => {
    if (isRefreshingRef.current || !isAuthenticated) return;

    const timeSinceLastRefresh = Date.now() - lastTokenRefreshRef.current;
    if (timeSinceLastRefresh < TOKEN_REFRESH_INTERVAL) return;

    isRefreshingRef.current = true;

    try {
      // Le refresh token transite par le cookie httpOnly (withCredentials) ; le body
      // n'est qu'un repli pour les sessions existantes sans cookie.
      const response = await axios.post(
        `${API_URL}/auth/refresh`,
        refreshToken ? { refreshToken } : {},
        { withCredentials: true }
      );

      if (response.data.success) {
        const { accessToken: newAccessToken, refreshToken: newRefreshToken, expiresIn } =
          response.data.data;

        setTokens({
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
          expiresIn,
        });

        lastTokenRefreshRef.current = Date.now();
      }
    } catch {
      // Si le refresh échoue, ne rien faire - l'intercepteur Axios gérera la 401
    } finally {
      isRefreshingRef.current = false;
    }
  }, [isAuthenticated, refreshToken, setTokens]);

  // Fonction de déconnexion
  const logout = useCallback(() => {
    // Nettoyer les timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    setShowWarning(false);
    storeLogout();
    onLogout?.();
  }, [storeLogout, onLogout]);

  // Fonction pour démarrer le compte à rebours d'avertissement
  const startWarningCountdown = useCallback(() => {
    setShowWarning(true);
    setSecondsRemaining(Math.floor(WARNING_BEFORE_LOGOUT / 1000));

    // Mettre à jour le compteur chaque seconde
    countdownIntervalRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          // Temps écoulé, déconnecter
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [logout]);

  // Fonction pour réinitialiser le timer d'inactivité
  const resetInactivityTimer = useCallback(() => {
    // Nettoyer les timers existants
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    // Cacher l'avertissement si affiché
    setShowWarning(false);
    setSecondsRemaining(Math.floor(WARNING_BEFORE_LOGOUT / 1000));

    lastActivityRef.current = Date.now();

    // Rafraîchir le token si nécessaire (proactivement)
    refreshTokenProactively();

    // Timer pour afficher l'avertissement
    warningTimerRef.current = setTimeout(() => {
      startWarningCountdown();
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);

    // Timer de déconnexion (backup, au cas où le countdown ne fonctionne pas)
    inactivityTimerRef.current = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT);
  }, [refreshTokenProactively, startWarningCountdown, logout]);

  // Fonction pour prolonger la session (appelée depuis la modal)
  const extendSession = useCallback(() => {
    resetInactivityTimer();
    // Forcer un rafraîchissement du token
    lastTokenRefreshRef.current = 0;
    refreshTokenProactively();
  }, [resetInactivityTimer, refreshTokenProactively]);

  // Gestionnaire d'événements d'activité
  const handleActivity = useCallback(() => {
    // Ne pas réinitialiser si la modal d'avertissement est affichée
    // (l'utilisateur doit cliquer sur "Continuer" explicitement)
    if (showWarning) return;

    // Throttle: ne pas traiter les événements trop fréquemment
    const now = Date.now();
    if (now - lastActivityRef.current < 1000) return;

    resetInactivityTimer();
  }, [showWarning, resetInactivityTimer]);

  // Effet pour configurer les écouteurs d'événements
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;

    // Initialiser le timer
    resetInactivityTimer();

    // Ajouter les écouteurs
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Écouter aussi les appels API réussis comme signe d'activité
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.ok && !showWarning) {
        lastActivityRef.current = Date.now();
      }
      return response;
    };

    return () => {
      // Nettoyer les écouteurs
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      // Restaurer fetch
      window.fetch = originalFetch;

      // Nettoyer les timers
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [enabled, isAuthenticated, handleActivity, resetInactivityTimer, showWarning]);

  return {
    showWarning,
    secondsRemaining,
    extendSession,
    logout,
  };
}
